import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import { FiCheck, FiChevronLeft, FiChevronRight, FiLock, FiPlus, FiX, FiZap } from "react-icons/fi";
import "./FichaCliente.css";

/*
 * El cliente rellena su ficha por pasos. De aquí sale todo lo demás: la
 * rutina, el plan de comidas y lo que el entrenador necesita saber.
 *
 * Se guarda al pasar de paso, no al final: si lo deja a medias, lo retoma.
 * El bloque de salud va al final, es voluntario y pide consentimiento
 * explícito antes de enseñar un solo campo.
 */

/* ─── Campos reutilizables ─── */

function Opciones({ valor, onChange, opciones, columnas = 2 }) {
  return (
    <div className="fc-opciones" style={{ "--cols": columnas }}>
      {opciones.map(([v, t]) => (
        <button
          key={v}
          type="button"
          className={valor === v ? "fc-opcion fc-opcion-on" : "fc-opcion"}
          onClick={() => onChange(v)}
          aria-pressed={valor === v}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// Lista de cosas que el cliente escribe: alergias, material, lesiones…
function Etiquetas({ valor = [], onChange, sugerencias = [], placeholder }) {
  const [texto, setTexto] = useState("");

  const anadir = (t) => {
    const limpio = t.trim();
    if (!limpio || valor.includes(limpio) || valor.length >= 20) return;
    onChange([...valor, limpio]);
    setTexto("");
  };

  const sinUsar = sugerencias.filter((s) => !valor.includes(s));

  return (
    <div className="fc-etiquetas">
      {valor.length > 0 && (
        <ul className="fc-puestas">
          {valor.map((v) => (
            <li key={v}>
              {v}
              <button type="button" onClick={() => onChange(valor.filter((x) => x !== v))} aria-label={`Quitar ${v}`}>
                <FiX />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="fc-anadir">
        <input
          type="text"
          value={texto}
          placeholder={placeholder}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              anadir(texto);
            }
          }}
        />
        <button type="button" onClick={() => anadir(texto)} disabled={!texto.trim()} aria-label="Añadir">
          <FiPlus />
        </button>
      </div>

      {sinUsar.length > 0 && (
        <ul className="fc-sugerencias">
          {sinUsar.slice(0, 10).map((s) => (
            <li key={s}>
              <button type="button" onClick={() => anadir(s)}>+ {s}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const Campo = ({ etiqueta, ayuda, obligatorio, children }) => (
  <div className="fc-campo">
    <span className="fc-etiqueta">
      {etiqueta}
      {obligatorio && <em aria-label="obligatorio">*</em>}
    </span>
    {ayuda && <span className="fc-ayuda">{ayuda}</span>}
    {children}
  </div>
);

/* ─── Los pasos ─── */

const PASOS = [
  { id: "ti", titulo: "Sobre ti", intro: "Para calcular tus necesidades con tus datos, no con una media." },
  { id: "objetivo", titulo: "Tu objetivo", intro: "Qué quieres conseguir y en cuánto tiempo." },
  { id: "entreno", titulo: "Entrenamiento", intro: "Tu nivel y de cuánto tiempo dispones de verdad." },
  { id: "vida", titulo: "Tu día a día", intro: "El descanso y el estrés cambian lo que se puede pedir al cuerpo." },
  { id: "comida", titulo: "Alimentación", intro: "Para que el plan sea algo que te vayas a comer." },
  { id: "gustos", titulo: "Preferencias", intro: "Lo que disfrutas y lo que no soportas." },
  { id: "salud", titulo: "Salud", intro: "Voluntario, pero es lo que evita que te mandemos algo que te haga daño." },
];

function FichaCliente({ onCompletada }) {
  const [datos, setDatos] = useState(null);
  const [paso, setPaso] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [porcentaje, setPorcentaje] = useState(0);
  const [hecho, setHecho] = useState(false);
  const [wa, setWa] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { token } = await getSession();
      const res = await axios.get(`${API_URL}/perfil`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const p = res.data.perfil || {};
      setDatos({
        ...p,
        consentimientoSalud: p.consentimientoSalud?.dado || false,
        fechaNacimiento: p.fechaNacimiento ? String(p.fechaNacimiento).slice(0, 10) : "",
      });
      setPorcentaje(res.data.porcentaje || 0);
      // El permiso de WhatsApp vive en el usuario, no en la ficha
      const est = await axios
        .get(`${API_URL}/whatsapp/mi-estado`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.data)
        .catch(() => null);
      setWa(est);
    } catch {
      setError("No se pudo cargar tu ficha.");
      setDatos({});
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const set = (campo) => (valor) => setDatos((d) => ({ ...d, [campo]: valor }));
  const input = (campo) => ({
    value: datos?.[campo] ?? "",
    onChange: (e) => setDatos((d) => ({ ...d, [campo]: e.target.value })),
  });

  // Cambiar el permiso de WhatsApp: se guarda al momento, aparte de la ficha
  const cambiarWa = async (activo) => {
    setWa((w) => ({ ...w, consentimiento: activo }));
    try {
      const { token } = await getSession();
      const res = await axios.put(
        `${API_URL}/whatsapp/consentimiento`,
        { activo, numero: datos.telefono || wa?.numero },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWa((w) => ({ ...w, consentimiento: res.data.consentimiento }));
    } catch (err) {
      setWa((w) => ({ ...w, consentimiento: !activo }));
      setError(err.response?.data?.error || "No se pudo guardar el permiso de WhatsApp.");
    }
  };

  const guardar = async (avanzar = true) => {
    setGuardando(true);
    setError("");
    try {
      const { token } = await getSession();
      const res = await axios.put(`${API_URL}/perfil`, datos, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPorcentaje(res.data.porcentaje);
      if (avanzar) {
        if (paso < PASOS.length - 1) setPaso((p) => p + 1);
        else {
          setHecho(true);
          onCompletada?.(res.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

  if (!datos) return <div className="app-estado">Cargando tu ficha…</div>;

  if (hecho) {
    return (
      <section className="fc-fin">
        <FiCheck className="fc-fin-icono" />
        <h2>Ficha completada</h2>
        <p>
          Tu entrenador ya tiene todo lo que necesita para prepararte la rutina y el
          plan de comidas. Puedes volver aquí y cambiar lo que quieras cuando quieras.
        </p>
        <button type="button" className="fc-btn" onClick={() => setHecho(false)}>
          Revisar mis respuestas
        </button>
      </section>
    );
  }

  const actual = PASOS[paso];

  return (
    <div className="fc">
      {/* Progreso */}
      <div className="fc-top">
        <div className="fc-barra">
          <div className="fc-barra-relleno" style={{ width: `${((paso + 1) / PASOS.length) * 100}%` }} />
        </div>
        <span className="fc-contador">
          Paso {paso + 1} de {PASOS.length}
          {porcentaje > 0 && ` · ${porcentaje}% completado`}
        </span>
      </div>

      <header className="fc-cabecera">
        <h2>{actual.titulo}</h2>
        <p>{actual.intro}</p>
      </header>

      <div className="fc-cuerpo">
        {/* 1 · Sobre ti */}
        {actual.id === "ti" && (
          <>
            <Campo etiqueta="Fecha de nacimiento" obligatorio>
              <input type="date" className="fc-input" {...input("fechaNacimiento")} />
            </Campo>
            <Campo etiqueta="Sexo" ayuda="El metabolismo basal se calcula distinto." obligatorio>
              <Opciones
                valor={datos.sexo}
                onChange={set("sexo")}
                opciones={[
                  ["hombre", "Hombre"],
                  ["mujer", "Mujer"],
                  ["otro", "Otro"],
                  ["prefiero-no-decir", "Prefiero no decirlo"],
                ]}
              />
            </Campo>
            <div className="fc-dos">
              <Campo etiqueta="Peso actual (kg)">
                <input type="number" step="0.1" min="30" max="300" className="fc-input" {...input("peso")} />
              </Campo>
              <Campo etiqueta="Altura (cm)">
                <input type="number" min="120" max="230" className="fc-input" {...input("altura")} />
              </Campo>
            </div>
            <Campo etiqueta="Ciudad">
              <input type="text" className="fc-input" maxLength={60} {...input("ciudad")} />
            </Campo>
            <Campo etiqueta="Móvil" ayuda="Para avisarte por WhatsApp si lo autorizas abajo.">
              <input
                type="tel"
                className="fc-input"
                maxLength={20}
                placeholder="600 00 00 00"
                {...input("telefono")}
              />
            </Campo>

            {wa?.disponible && (
              <label className="fc-consentimiento">
                <input
                  type="checkbox"
                  checked={!!wa.consentimiento}
                  onChange={(e) => cambiarWa(e.target.checked)}
                />
                <span>
                  <FiZap aria-hidden="true" /> Quiero que me aviséis por WhatsApp cuando
                  mi entrenador me escriba o lleve días sin verme entrenar. Puedo
                  quitarlo cuando quiera desde aquí, o respondiendo BAJA al mensaje.
                </span>
              </label>
            )}
          </>
        )}

        {/* 2 · Objetivo */}
        {actual.id === "objetivo" && (
          <>
            <Campo etiqueta="¿Qué quieres conseguir?" obligatorio>
              <Opciones
                columnas={1}
                valor={datos.objetivoPrincipal}
                onChange={set("objetivoPrincipal")}
                opciones={[
                  ["perder-grasa", "Perder grasa"],
                  ["ganar-musculo", "Ganar músculo"],
                  ["recomposicion", "Perder grasa y ganar músculo a la vez"],
                  ["mantener", "Mantenerme como estoy"],
                  ["mejorar-salud", "Encontrarme mejor de salud"],
                  ["rendimiento", "Rendir más en mi deporte"],
                ]}
              />
            </Campo>
            <Campo etiqueta="Peso al que te gustaría llegar (kg)" ayuda="Si no lo tienes claro, déjalo vacío.">
              <input type="number" step="0.5" min="30" max="300" className="fc-input" {...input("pesoObjetivo")} />
            </Campo>
            <Campo etiqueta="¿En cuánto tiempo?" obligatorio>
              <Opciones
                valor={datos.plazo}
                onChange={set("plazo")}
                opciones={[
                  ["1-mes", "1 mes"],
                  ["3-meses", "3 meses"],
                  ["6-meses", "6 meses"],
                  ["1-ano", "1 año"],
                  ["sin-prisa", "Sin prisa"],
                ]}
              />
            </Campo>
            <Campo
              etiqueta="¿Por qué ahora?"
              ayuda="Lo leerá tu entrenador. Sirve para recordártelo el día que te cueste."
            >
              <textarea rows={3} className="fc-input" maxLength={600} {...input("motivacion")} />
            </Campo>
            <Campo etiqueta="¿Has intentado algo antes? ¿Qué pasó?">
              <textarea rows={3} className="fc-input" maxLength={600} {...input("intentosPrevios")} />
            </Campo>
          </>
        )}

        {/* 3 · Entrenamiento */}
        {actual.id === "entreno" && (
          <>
            <Campo etiqueta="¿Cuánta experiencia tienes?" obligatorio>
              <Opciones
                columnas={1}
                valor={datos.nivel}
                onChange={set("nivel")}
                opciones={[
                  ["nunca", "Nunca he entrenado"],
                  ["principiante", "Menos de un año"],
                  ["intermedio", "Entre uno y tres años"],
                  ["avanzado", "Más de tres años"],
                ]}
              />
            </Campo>
            <Campo etiqueta="Días a la semana" ayuda="Sé realista: mejor tres que cumples que cinco que no." obligatorio>
              <Opciones
                columnas={4}
                valor={datos.diasPorSemana}
                onChange={set("diasPorSemana")}
                opciones={[1, 2, 3, 4, 5, 6, 7].map((n) => [n, String(n)])}
              />
            </Campo>
            <Campo etiqueta="Minutos por sesión" obligatorio>
              <Opciones
                columnas={3}
                valor={datos.minutosPorSesion}
                onChange={set("minutosPorSesion")}
                opciones={[
                  [30, "30 min"],
                  [45, "45 min"],
                  [60, "1 hora"],
                  [90, "1 h 30"],
                  [120, "2 horas"],
                ]}
              />
            </Campo>
            <Campo etiqueta="¿Dónde entrenas?" obligatorio>
              <Etiquetas
                valor={datos.dondeEntrena}
                onChange={set("dondeEntrena")}
                placeholder="Escribe y pulsa Enter"
                sugerencias={["gimnasio", "en casa", "al aire libre", "piscina", "parque"]}
              />
            </Campo>
            <Campo etiqueta="¿Con qué material cuentas?">
              <Etiquetas
                valor={datos.material}
                onChange={set("material")}
                placeholder="Mancuernas, bandas…"
                sugerencias={[
                  "mancuernas", "barra y discos", "bandas elásticas", "kettlebell",
                  "máquinas", "banco", "barra de dominadas", "esterilla", "nada",
                ]}
              />
            </Campo>
            <Campo etiqueta="¿A qué hora te viene mejor?">
              <Opciones
                columnas={3}
                valor={datos.momentoDelDia}
                onChange={set("momentoDelDia")}
                opciones={[
                  ["manana", "Mañana"],
                  ["mediodia", "Mediodía"],
                  ["tarde", "Tarde"],
                  ["noche", "Noche"],
                  ["variable", "Depende"],
                ]}
              />
            </Campo>
          </>
        )}

        {/* 4 · Día a día */}
        {actual.id === "vida" && (
          <>
            <Campo etiqueta="¿Cómo es tu trabajo?" obligatorio>
              <Opciones
                columnas={1}
                valor={datos.trabajo}
                onChange={set("trabajo")}
                opciones={[
                  ["sedentario", "Sentado casi todo el día"],
                  ["de-pie", "De pie, pero sin esfuerzo"],
                  ["fisico", "Me muevo bastante"],
                  ["muy-fisico", "Trabajo físico duro"],
                ]}
              />
            </Campo>
            <Campo etiqueta="¿Cuántas horas duermes?" obligatorio>
              <Opciones
                columnas={4}
                valor={datos.horasSueno}
                onChange={set("horasSueno")}
                opciones={[[5, "5 o menos"], [6, "6"], [7, "7"], [8, "8"], [9, "9 o más"]]}
              />
            </Campo>
            <Campo etiqueta="¿Y qué tal duermes?">
              <Opciones
                columnas={3}
                valor={datos.calidadSueno}
                onChange={set("calidadSueno")}
                opciones={[["mala", "Mal"], ["regular", "Regular"], ["buena", "Bien"]]}
              />
            </Campo>
            <Campo etiqueta="Nivel de estrés" obligatorio>
              <Opciones
                columnas={3}
                valor={datos.estres}
                onChange={set("estres")}
                opciones={[["bajo", "Bajo"], ["medio", "Medio"], ["alto", "Alto"]]}
              />
            </Campo>
            <Campo etiqueta="¿Cuánto te mueves al día?">
              <Opciones
                columnas={2}
                valor={datos.pasosDiarios}
                onChange={set("pasosDiarios")}
                opciones={[
                  ["menos-5000", "Poco"],
                  ["5000-10000", "Normal"],
                  ["mas-10000", "Mucho"],
                  ["no-lo-se", "No lo sé"],
                ]}
              />
            </Campo>
            <Campo etiqueta="Alcohol">
              <Opciones
                columnas={2}
                valor={datos.alcohol}
                onChange={set("alcohol")}
                opciones={[
                  ["nunca", "Nunca"],
                  ["ocasional", "De vez en cuando"],
                  ["fin-de-semana", "Fines de semana"],
                  ["diario", "A diario"],
                ]}
              />
            </Campo>
          </>
        )}

        {/* 5 · Alimentación */}
        {actual.id === "comida" && (
          <>
            <Campo etiqueta="¿Sigues algún tipo de dieta?" obligatorio>
              <Opciones
                columnas={2}
                valor={datos.tipoDieta}
                onChange={set("tipoDieta")}
                opciones={[
                  ["omnivora", "Como de todo"],
                  ["vegetariana", "Vegetariana"],
                  ["vegana", "Vegana"],
                  ["pescetariana", "Pescetariana"],
                  ["sin-gluten", "Sin gluten"],
                  ["otra", "Otra"],
                ]}
              />
            </Campo>
            <Campo
              etiqueta="Alergias"
              ayuda="Importante: el plan no incluirá nada de esto."
            >
              <Etiquetas
                valor={datos.alergias}
                onChange={set("alergias")}
                placeholder="Frutos secos, marisco…"
                sugerencias={["frutos secos", "marisco", "huevo", "soja", "pescado", "gluten"]}
              />
            </Campo>
            <Campo etiqueta="Intolerancias">
              <Etiquetas
                valor={datos.intolerancias}
                onChange={set("intolerancias")}
                placeholder="Lactosa, fructosa…"
                sugerencias={["lactosa", "gluten", "fructosa", "sorbitol"]}
              />
            </Campo>
            <Campo etiqueta="¿Qué no te comes aunque puedas?">
              <Etiquetas
                valor={datos.alimentosQueNoCome}
                onChange={set("alimentosQueNoCome")}
                placeholder="Lo que no soportas"
                sugerencias={["pescado", "setas", "cebolla", "tomate", "verduras amargas", "casquería"]}
              />
            </Campo>
            <Campo etiqueta="¿Qué te encanta?" ayuda="Lo meteremos en el plan siempre que encaje.">
              <Etiquetas
                valor={datos.alimentosFavoritos}
                onChange={set("alimentosFavoritos")}
                placeholder="Tus comidas favoritas"
                sugerencias={["pollo", "arroz", "pasta", "huevos", "aguacate", "salmón", "yogur"]}
              />
            </Campo>
            <Campo etiqueta="¿Cuántas comidas haces al día?" obligatorio>
              <Opciones
                columnas={3}
                valor={datos.comidasAlDia}
                onChange={set("comidasAlDia")}
                opciones={[[2, "2"], [3, "3"], [4, "4"], [5, "5"], [6, "6"]]}
              />
            </Campo>
            <Campo etiqueta="¿Cocinas en casa?" obligatorio>
              <Opciones
                columnas={2}
                valor={datos.cocinaEnCasa}
                onChange={set("cocinaEnCasa")}
                opciones={[
                  ["siempre", "Siempre"],
                  ["casi-siempre", "Casi siempre"],
                  ["a-veces", "A veces"],
                  ["casi-nunca", "Casi nunca"],
                ]}
              />
            </Campo>
            <Campo etiqueta="Presupuesto para la compra">
              <Opciones
                columnas={3}
                valor={datos.presupuesto}
                onChange={set("presupuesto")}
                opciones={[
                  ["ajustado", "Ajustado"],
                  ["normal", "Normal"],
                  ["sin-limite", "Sin problema"],
                ]}
              />
            </Campo>
            <Campo etiqueta="Suplementos que tomas">
              <Etiquetas
                valor={datos.suplementos}
                onChange={set("suplementos")}
                placeholder="Proteína, creatina…"
                sugerencias={["proteína", "creatina", "omega 3", "vitamina D", "magnesio", "ninguno"]}
              />
            </Campo>
          </>
        )}

        {/* 6 · Preferencias */}
        {actual.id === "gustos" && (
          <>
            <Campo etiqueta="¿Qué ejercicios disfrutas?">
              <Etiquetas
                valor={datos.ejerciciosQueGustan}
                onChange={set("ejerciciosQueGustan")}
                placeholder="Lo que te gusta hacer"
                sugerencias={[
                  "sentadillas", "peso muerto", "press banca", "dominadas", "correr",
                  "bici", "natación", "boxeo", "yoga", "pilates", "crossfit",
                ]}
              />
            </Campo>
            <Campo etiqueta="¿Y cuáles odias?" ayuda="Intentaremos evitarlos o cambiarlos por otros.">
              <Etiquetas
                valor={datos.ejerciciosQueOdia}
                onChange={set("ejerciciosQueOdia")}
                placeholder="Lo que no soportas"
                sugerencias={["burpees", "cinta", "abdominales", "zancadas", "saltos", "planchas"]}
              />
            </Campo>
            <Campo etiqueta="¿Practicas algún deporte?">
              <Etiquetas
                valor={datos.deportes}
                onChange={set("deportes")}
                placeholder="Fútbol, pádel…"
                sugerencias={["fútbol", "pádel", "tenis", "baloncesto", "running", "ciclismo", "surf"]}
              />
            </Campo>
          </>
        )}

        {/* 7 · Salud */}
        {actual.id === "salud" && (
          <>
            <div className="fc-privacidad">
              <FiLock />
              <div>
                <strong>Esto es voluntario y es privado</strong>
                <p>
                  Lo que escribas aquí lo ve sólo tu entrenador, y sirve para no mandarte
                  nada que pueda hacerte daño. Puedes saltarte este paso, y puedes retirar
                  el permiso cuando quieras: al hacerlo, estos datos se borran.
                </p>
              </div>
            </div>

            <label className="fc-consentimiento">
              <input
                type="checkbox"
                checked={!!datos.consentimientoSalud}
                onChange={(e) => setDatos({ ...datos, consentimientoSalud: e.target.checked })}
              />
              <span>
                Autorizo a Titan Training a guardar y tratar estos datos de salud con el
                único fin de adaptar mi entrenamiento y mi alimentación.
              </span>
            </label>

            {datos.consentimientoSalud && (
              <>
                <Campo etiqueta="¿Tienes alguna lesión ahora mismo?">
                  <Etiquetas
                    valor={datos.lesionesActuales}
                    onChange={set("lesionesActuales")}
                    placeholder="Describe la lesión"
                  />
                </Campo>
                <Campo etiqueta="¿Y lesiones anteriores que te marcaron?">
                  <Etiquetas
                    valor={datos.lesionesPasadas}
                    onChange={set("lesionesPasadas")}
                    placeholder="Lesiones pasadas"
                  />
                </Campo>
                <Campo etiqueta="¿Dónde te suele molestar?">
                  <Etiquetas
                    valor={datos.zonasMolestia}
                    onChange={set("zonasMolestia")}
                    placeholder="Zona"
                    sugerencias={["hombro", "lumbar", "cervical", "rodilla", "cadera", "codo", "muñeca", "tobillo"]}
                  />
                </Campo>
                <Campo etiqueta="Condiciones médicas diagnosticadas">
                  <Etiquetas
                    valor={datos.condicionesMedicas}
                    onChange={set("condicionesMedicas")}
                    placeholder="Lo que te hayan diagnosticado"
                  />
                </Campo>
                <Campo etiqueta="Medicación que tomas">
                  <Etiquetas
                    valor={datos.medicacion}
                    onChange={set("medicacion")}
                    placeholder="Nombre del medicamento"
                  />
                </Campo>
                <Campo etiqueta="¿Te han operado en el último año?">
                  <Etiquetas
                    valor={datos.cirugiasRecientes}
                    onChange={set("cirugiasRecientes")}
                    placeholder="Qué operación y cuándo"
                  />
                </Campo>
                <Campo etiqueta="¿Estás embarazada o has dado a luz hace poco?">
                  <Opciones
                    columnas={3}
                    valor={datos.embarazoOPostparto}
                    onChange={set("embarazoOPostparto")}
                    opciones={[["no", "No"], ["embarazo", "Embarazo"], ["postparto", "Postparto"]]}
                  />
                </Campo>
                <Campo
                  etiqueta="¿Tu médico sabe que vas a entrenar?"
                  ayuda="Si tienes alguna condición, conviene que lo sepa."
                >
                  <Opciones
                    columnas={2}
                    valor={datos.autorizacionMedica}
                    onChange={set("autorizacionMedica")}
                    opciones={[
                      ["si", "Sí, me ha dado el visto bueno"],
                      ["no", "No lo he consultado"],
                      ["no-hace-falta", "Estoy sano, no hace falta"],
                      ["no-lo-se", "No lo sé"],
                    ]}
                  />
                </Campo>
                <Campo etiqueta="Cualquier otra cosa que debamos saber">
                  <textarea rows={3} className="fc-input" maxLength={1000} {...input("notasSalud")} />
                </Campo>
              </>
            )}
          </>
        )}
      </div>

      {error && <p className="fc-error">{error}</p>}

      <nav className="fc-nav">
        <button
          type="button"
          className="fc-btn-plano"
          onClick={() => setPaso((p) => Math.max(0, p - 1))}
          disabled={paso === 0}
        >
          <FiChevronLeft /> Atrás
        </button>
        <button type="button" className="fc-btn" onClick={() => guardar(true)} disabled={guardando}>
          {guardando
            ? "Guardando…"
            : paso === PASOS.length - 1
            ? "Terminar"
            : "Siguiente"}
          {!guardando && paso < PASOS.length - 1 && <FiChevronRight />}
        </button>
      </nav>
    </div>
  );
}

export default FichaCliente;
